import React, { useMemo, useState } from "react";
import { faGlobe } from "@fortawesome/free-solid-svg-icons";
import type { DevToolsPlugin } from "../../types";
import { ChipGridAccordion } from "../../FilterableChipGrid";

interface Site {
  name: string;
  description: string;
  href: string;
}

interface SitesPluginProps {
  mainSites: Site[];
  ukSites: Site[];
  usSites: Site[];
  verticals: Record<string, string>;
}

const sortAlphabetically = (sites: Site[]) =>
  [...sites].sort((a, b) => a.description.localeCompare(b.description));

function filterSites(sites: Site[], query: string): Site[] {
  return sites.filter((s) => s.description.toLowerCase().includes(query));
}

function SitesPanel({ mainSites, ukSites, usSites, verticals }: SitesPluginProps) {
  const [query, setQuery] = useState("");
  const verticalEntries = Object.entries(verticals);
  const sortedUkSites = useMemo(() => sortAlphabetically(ukSites), [ukSites]);
  const sortedUsSites = useMemo(() => sortAlphabetically(usSites), [usSites]);

  const lower = query.toLowerCase();
  const isFiltering = lower.length > 0;
  const filteredMain = useMemo(() => filterSites(mainSites, lower), [mainSites, lower]);
  const filteredUk = useMemo(() => filterSites(sortedUkSites, lower), [sortedUkSites, lower]);
  const filteredUs = useMemo(() => filterSites(sortedUsSites, lower), [sortedUsSites, lower]);
  const filteredVerticals = useMemo(
    () => verticalEntries.filter(([name]) => name.toLowerCase().includes(lower)),
    [verticalEntries, lower],
  );

  return (
    <>
      <input
        className="dt-form-input dt-filterable-chip-grid__search"
        type="search"
        placeholder="Filter..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      <ChipGridAccordion
        title="Main Sites"
        totalCount={mainSites.length}
        items={filteredMain}
        isFiltering={isFiltering}
        itemKey={(s) => s.name}
        itemLabel={(s) => s.description}
        itemHref={(s) => s.href}
      />

      <ChipGridAccordion
        title="UK Partners"
        totalCount={sortedUkSites.length}
        items={filteredUk}
        isFiltering={isFiltering}
        itemKey={(s) => s.name}
        itemLabel={(s) => s.description}
        itemHref={(s) => s.href}
      />

      <ChipGridAccordion
        title="US Partners"
        totalCount={sortedUsSites.length}
        items={filteredUs}
        isFiltering={isFiltering}
        itemKey={(s) => s.name}
        itemLabel={(s) => s.description}
        itemHref={(s) => s.href}
      />

      <ChipGridAccordion
        title="Verticals"
        totalCount={verticalEntries.length}
        items={filteredVerticals}
        isFiltering={isFiltering}
        itemKey={([name]) => name}
        itemLabel={([name]) => name}
        itemHref={([, href]) => href}
      />
    </>
  );
}

export function sitesPlugin(props: SitesPluginProps): DevToolsPlugin {
  return {
    id: "sites",
    name: "Sites & Verticals",
    icon: faGlobe,
    render: () => <SitesPanel {...props} />,
  };
}
